import { useState } from 'react';

import type { ConditionPanelHost } from '@/types/advanceFilter';

/**
 * Open/anchor/close plumbing shared by every condition popover. A condition is
 * either hosted — the filter strip owns the popover and anchors it to a chip,
 * so there is no trigger button and the popover is open from mount — or
 * self-managed, where it renders its own trigger and opens on click.
 *
 * `triggerRef` is a callback ref on purpose: storing the element in state is
 * what re-renders the popover once its anchor exists.
 */
export const useConditionPanel = (
  host: ConditionPanelHost | undefined,
  onPanelClose?: () => void
) => {
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(!!host);

  const close = () => {
    setIsOpen(false);
    onPanelClose?.();
    host?.onClose();
  };

  return {
    isHosted: !!host,
    isOpen,
    triggerRef: setTriggerEl,
    anchorEl: host?.anchorEl ?? triggerEl,
    close,
    // The popover is not modal, so a click on the trigger reaches it while the panel is open instead of being
    // swallowed by a backdrop — which makes the trigger, rather than the dismissed backdrop, the thing that closes it.
    toggle: () => (isOpen ? close() : setIsOpen(true)),
  };
};
